import { authenticateAndFetchData } from "@lib/apis";
import React, { useState } from "react";
import { Button, ButtonProps } from "@mui/material";
import { Downloading } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { getExportFilename } from "@lib/utils";
import { ColDef } from "ag-grid-community";
import {useAuth} from "../admin/AuthProvider";
import { GridColDef } from "@mui/x-data-grid";

export interface CSVColumn<T> {
    header: string;
    /** Either the field key name or a function to format the cell value */
    accessor: keyof T | ((row: T) => any);
}


export const exportToCSV = <T extends Record<string, any>>(
    data: T[],
    columns: CSVColumn<T>[],
    filename: string = "export"
): boolean => {
    if (!data || data.length === 0) {
        return false;
    }

    const escapeCSV = (value: any) => {
        if (value == null) return '""';
        const stringValue = String(value);
        return `"${stringValue.replace(/"/g, '""')}"`;
    };

    // 1. Build Headers
    const headers = columns.map((col) => escapeCSV(col.header));

    // 2. Build Rows using accessors
    const rows = data.map((row) =>
        columns.map((col) => {
            const rawValue =
                typeof col.accessor === "function"
                    ? col.accessor(row)
                    : row[col.accessor];
            return escapeCSV(rawValue);
        })
    );

    // 3. Combine header + rows with UTF-8 BOM (\uFEFF) for Excel compatibility
    const csvContent =
        "\uFEFF" + [headers, ...rows].map((row) => row.join(",")).join("\n");

    // 4. Trigger file download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const sanitizedFilename = filename.endsWith(".csv") ? filename : `${filename}.csv`;

    link.href = url;
    link.download = sanitizedFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return true;
};


interface GenericCsvExportButtonProps<T> extends Omit<ButtonProps, "onClick"> {
    endpoint: string;
    queryParams: Record<string, string | number | boolean | undefined>;
    columns: CSVColumn<T>[];
    exportName: string; 
    filePrefix?: string;
    buttonText?: string;
    successMessage?: string;
    transformResponse?: (response: any) => T[];
}

export const GenericCsvExportButton = <T extends Record<string, any>>({
    endpoint,
    queryParams,
    columns,
    exportName,
    filePrefix,
    buttonText = "Export",
    successMessage,
    transformResponse,
    disabled,
    variant = "contained",
    color = "primary",
    startIcon = <Downloading />,
    ...buttonProps
}: GenericCsvExportButtonProps<T>) => {
    const { user } = useAuth();
    const [exportLoading, setExportLoading] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const handleExport = async () => {
        setExportLoading(true);

        try {
            // Filter out undefined/null/empty query parameters
            const cleanParams = new URLSearchParams();
            Object.entries({ ...queryParams, export_all: "true" }).forEach(([key, val]) => {
                if (val !== undefined && val !== null && val !== "") {
                    cleanParams.append(key, String(val));
                }
            });

            const response = await authenticateAndFetchData(`${endpoint}?${cleanParams.toString()}`);            

            const dataToExport: T[] = transformResponse 
                ? transformResponse(response)
                : Array.isArray(response?.data?.data) 
                    ? response.data.data 
                    : Array.isArray(response?.data) 
                        ? response.data 
                        : [];

            if (!dataToExport.length) {
                enqueueSnackbar(`No ${exportName.replace(/_/g, " ").toLowerCase()} available to export.`, {
                    variant: "error",
                });
                return;
            }

            const fileName = getExportFilename(user?.email ?? "unknown", exportName);
            const success = exportToCSV(dataToExport, columns, fileName);

            if (success) {
                enqueueSnackbar(
                    successMessage || `${exportName.replace(/_/g, " ")} exported successfully!`,
                    { variant: "success" }
                );
            } else {
                enqueueSnackbar("Failed to generate CSV file.", { variant: "error" });
            }
        } catch (err) {
            console.error(`Export error [${exportName}]:`, err);
            enqueueSnackbar(`Error while exporting ${exportName.replace(/_/g, " ").toLowerCase()}.`, {
                variant: "error",
            });
        } finally {
            setExportLoading(false);
        }
    };

    return (
        <Button
            startIcon={startIcon}
            variant={variant}
            color={color}
            onClick={handleExport}
            disabled={exportLoading || disabled}
            {...buttonProps}
        >
            {exportLoading ? "Exporting..." : buttonText}
        </Button>
    );
};


const getNestedValue = (obj: any, path: string) => {
    if (!obj || !path) return undefined;
    return path.split(".").reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);
};

export const colDefsToCsvColumns = <T extends Record<string, any>>(
    colDefs: ColDef[]
): CSVColumn<T>[] => {
    return colDefs
        .filter((col) => col.field || col.headerName)
        .filter((col) => !col.checkboxSelection && col.headerName !== "Actions" && col.field !== "action" && col.field !== "actions")
        .map((col) => ({
            header: col.headerName || String(col.field || ""),
            accessor: (row: T) => {
                try {
                    if (!row) return "";

                    let value = col.field ? getNestedValue(row, col.field) : undefined;

                    // 1. Handle fields with explicit link associations (e.g., postal_id -> postal_link)
                    if (col.field === "postal_id" && row.postal_link && value) {
                        // Standard CSV formula format readable by Excel / Google Sheets
                        return `=HYPERLINK("${row.postal_link}", "${value}")`;
                    }

                    // 2. Handle AG Grid valueGetter
                    if (col.valueGetter) {
                        if (typeof col.valueGetter === "function") {
                            value = col.valueGetter({
                                data: row,
                                node: null,
                                colDef: col,
                                getValue: (field: string) => getNestedValue(row, field),
                            } as any);
                        } else if (typeof col.valueGetter === "string" && col.field) {
                            value = getNestedValue(row, col.field);
                        }
                    }

                    // 3. Handle AG Grid valueFormatter
                    if (typeof col.valueFormatter === "function") {
                        const formatted = col.valueFormatter({
                            value,
                            data: row,
                            node: null,
                            colDef: col,
                        } as any);
                        
                        if (formatted !== undefined && formatted !== null && !React.isValidElement(formatted)) {
                            return String(formatted);
                        }
                    }

                    // 4. Handle Booleans
                    if (typeof value === "boolean") {
                        return value ? "Yes" : "No";
                    }

                    // 5. Handle Dates
                    if (value instanceof Date) {
                        return value.toISOString();
                    }

                    
                    if (typeof value === "object" && value !== null) {
                        if (Array.isArray(value)) {
                            return value.join(", "); // Returns "Campaign A, Campaign B" instead of '["Campaign A","Campaign B"]'
                        }
                        return JSON.stringify(value);
                    }
                                        
                    if (typeof value === "object" && value !== null) {
                        return JSON.stringify(value);
                    }

                    return value ?? "";
                } catch (err) {
                    console.warn(`[colDefsToCsvColumns] Error parsing column "${col.headerName || col.field}":`, err);
                    return "";
                }
            },
        }));
};

export const gridColDefsToCsvColumns = <T extends Record<string, any>>(
    colDefs: GridColDef[]
): CSVColumn<T>[] => {
    return colDefs
        .filter((col) => col.field && col.headerName)
        .filter((col) => col.field !== "actions" && col.field !== "action")
        .map((col) => ({
            header: col.headerName || col.field,
            accessor: (row: T) => {
                try {
                    if (!row) return "";

                    let value = row[col.field];

                    // 1. Handle valueGetter if defined
                    if (typeof col.valueGetter === "function") {
                        value = (col.valueGetter as Function)(value, row, col, {});
                    }

                    // 2. Handle valueFormatter if defined
                    if (typeof col.valueFormatter === "function") {
                        const formatter = col.valueFormatter as Function;
                        let formatted: any;

                        // Check function arity to support both MUI DataGrid v7+ (4 args) and v6 (1 arg)
                        if (formatter.length > 1) {
                            // v7+: (value, row, column, apiRef)
                            formatted = formatter(value, row, col, {});
                        } else {
                            // v5 / v6: ({ value, row, field, api })
                            formatted = formatter({
                                value,
                                row,
                                field: col.field,
                                api: {} as any,
                            });
                        }

                        if (formatted !== undefined && formatted !== null) {
                            return String(formatted);
                        }
                    }

                    // 3. Handle Dates
                    if (col.field === "added_on" && value) {
                        return new Date(value).toLocaleDateString();
                    }

                    // 4. Handle Booleans
                    if (typeof value === "boolean") {
                        return value ? "Yes" : "No";
                    }

                    // 5. Handle Objects/Arrays
                    if (typeof value === "object" && value !== null) {
                        return JSON.stringify(value);
                    }

                    return value ?? "";
                } catch (err) {
                    console.warn(`[gridColDefsToCsvColumns] Error parsing column "${col.headerName || col.field}":`, err);
                    return "";
                }
            },
        }));
};